import { BrButton, BrDivider, BrLoading } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import CustomConfirmer from 'components/customConfirmer/customConfirmer';
import CustomDivider from 'components/customDivider/customDivider';
import CustomStep from 'components/customStep/customStep';
import ErrorMessage from 'components/errorMessage/errorMessage';
import ModalOpcoesPagamento from 'components/modalOpcoesPagamento/modalOpcoesPagamento';
import StepFormAccordionConfig from 'components/stepFormAccordionConfig/stepFormAccordionConfig';
import { ArrecadacaoContext } from 'contexts/ArrecadacaoContext';
import { useNotificacao } from 'hooks/useNotificacao';
import { PaginasOutrosRegimes } from 'interfaces/IBasesCalculo';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { INovaGuiaRecolhimento } from 'interfaces/IGuiaRecolhimento';
import { useContext, useEffect, useState } from 'react';
import { Button, Modal } from 'react-dsgov';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import TituloSection from '../../../components/tituloSection/tituloSection';
import { ICrumb } from '../../../interfaces/ICrumb';
import ResumoSection from '../../components/resumoSection/resumoSection';
import { useBackEndRequests } from '../lib/backEndRequests';
import BalnearioForm from './components/balnearioForm/balnearioForm';
import DadosOperacoes from './components/dadosOperacoes/dadosOperacoes';
import { validarDadosRecolhimento } from './components/dadosRecolhimento/dadoRecolhimentoValidacoes';
import DadosRecolhimento from './components/dadosRecolhimento/dadosRecolhimento';
import OutrasBaseCalculoForm from './components/outrasBaseCalculoForm/outrasBaseCalculoForm';
import styles from './TitularArrendatarioEdit.module.scss';

interface ITitularArrendatarioEditProps {
  stepProp?: number;
}

function TitularArrendatarioEdit({ stepProp }: Readonly<ITitularArrendatarioEditProps>) {
  const [step, setStep] = useState(stepProp || 1);
  const [isAdicaoNovaBaseCalculo, setIsAdicaoNovaBaseCalculo] = useState(false);
  const [baseDeCalculoEdicao, setBaseDeCalculoEdicao] = useState<PaginasOutrosRegimes | ''>('');
  const [isEdicaoBaseCalculo, setIsEdicaoBaseCalculo] = useState(false);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isModalOpcoesPagamentoOpened, setIsModalOpcoesPagamentoOpened] = useState(false);
  const [isEdicaoDadosRecolhimento, setIsEdicaoDadosRecolhimento] = useState(false);
  const [opcaoPagamento, setOpcaoPagamento] = useState('');

  const [isLoadingSalvarDadosRecolhimento, setIsLoadingSalvarDadosRecolhimento] = useState(false);
  const [isLoadingCopiarGuia, setIsLoadingCopiarGuia] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';

  const userCpfCnpj = sessionStorage.getItem('userCpfCnpj');

  const navigate = useNavigate();
  const {
    postGuiaRecolhimento,
    putGuiaRecolhimento,
    deleteGuiaRecolhimento,
    getPermissaoAcessarGuiaRecolhimento,
    getCopiarGuia,
    getDadosRecolhimento,
  } = useBackEndRequests();

  const [dadosRecolhimento, setDadosRecolhimento] = useState<INovaGuiaRecolhimento>({
    id: guiaRecolhimentoId,
    tipoDeclarante: 0,
    cpfCnpjDeclarante: userCpfCnpj || '',
    competenciaMes: 0,
    competenciaAno: 0,
    numeroProcesso: '',
    substancia: '',
    municipio: -1,
    tipoRelacao: -1,
    observacoes: '',
  });

  const context = useContext(ArrecadacaoContext);
  const mostrarNotificacao = useNotificacao();

  const steps = [
    { label: 'Informações do emitente' },
    { label: 'Dados do recolhimento' },
    { label: 'Dados das operações' },
    { label: 'Guia de recolhimento' },
    { label: 'Resumo' },
  ];

  const {
    data: permissaoStatus,
    isLoading: isLoadingPermissaoAcesso,
    error: permissaoAcessoError,
    refetch: permissaoAcessoRefetch,
  } = useQuery<number, IErrorWithSentryId>({
    queryKey: ['getPermissaoAcessarGuiaRecolhimento', guiaRecolhimentoId, userCpfCnpj],
    queryFn: () => getPermissaoAcessarGuiaRecolhimento(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
    retryDelay: 100,
  });

  useEffect(() => {
    const crumbs: ICrumb[] = [
      {
        isHome: true,
        label: 'home',
        onClick: () => {
          navigate('/');
        },
      },
      {
        label: 'Titular ou Arrendatário',
      },
      {
        active: true,
        label: 'Recolhimento da CFEM',
      },
    ];
    context?.setCrumbs(crumbs);

    const isPessoaNaoCadastrada = sessionStorage.getItem('isPessoaNaoCadastrada') === 'true';
    const isFilial = sessionStorage.getItem('naturezaDocumentacao') === 'filial';
    if (isPessoaNaoCadastrada || isFilial) {
      navigate('/');
    }
  }, [navigate]);

  const baseDeCalculo = () => {
    if (baseDeCalculoEdicao === 'balnearioComBanho' || baseDeCalculoEdicao === 'balnearioSemBanho')
      return (
        <BalnearioForm
          isEdicao={isEdicaoBaseCalculo}
          setIsEdicao={setIsEdicaoBaseCalculo}
          comBanho={baseDeCalculoEdicao === 'balnearioComBanho'}
          setIsAdicaoNovaBaseCalculo={setIsAdicaoNovaBaseCalculo}
        />
      );
    else if (baseDeCalculoEdicao)
      return (
        <OutrasBaseCalculoForm
          isEdicao={isEdicaoBaseCalculo}
          setIsEdicao={setIsEdicaoBaseCalculo}
          nomeBaseCalculo={baseDeCalculoEdicao}
          setIsAdicaoNovaBaseCalculo={setIsAdicaoNovaBaseCalculo}
        />
      );
    else setIsAdicaoNovaBaseCalculo(false);
  };

  const enviarDadosRecolhimento = async () => {
    setIsLoadingSalvarDadosRecolhimento(true);
    if (dadosRecolhimento.id !== guiaRecolhimentoId)
      setDadosRecolhimento((prev) => ({ ...prev, id: guiaRecolhimentoId }));

    if (dadosRecolhimento.id === '' || dadosRecolhimento.cpfCnpjDeclarante === '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Ocorreu um erro ao iniciar o recolhimento. Por favor, tente novamente mais tarde.',
      });
    } else {
      const msgErroValidacao = validarDadosRecolhimento(dadosRecolhimento, isEdicaoDadosRecolhimento);

      if (msgErroValidacao === '') {
        if (isEdicaoDadosRecolhimento) {
          await putGuiaRecolhimento(dadosRecolhimento)
            .then(async () => {
              mostrarNotificacao({
                tipo: 'success',
                conteudo: 'Guia de Recolhimento salva com sucesso',
              });
              setStep(step + 1);
            })
            .catch((err) => {
              mostrarNotificacao({
                tipo: 'danger',
                conteudo: err?.response?.data?.message,
                ...(err && { complementoSentryId: err }),
              });
            })
            .finally(() => {
              setIsLoadingSalvarDadosRecolhimento(false);
            });
        } else {
          await postGuiaRecolhimento(dadosRecolhimento)
            .then(async () => {
              mostrarNotificacao({
                tipo: 'success',
                conteudo: 'Guia de Recolhimento iniciada com sucesso',
              });
              setStep(step + 1);
              context?.setNumeroProcesso(dadosRecolhimento.numeroProcesso);
            })
            .catch((err) => {
              mostrarNotificacao({
                tipo: 'danger',
                conteudo: err?.response?.data?.message,
                ...(err && { complementoSentryId: err }),
              });
            })
            .finally(() => {
              setIsLoadingSalvarDadosRecolhimento(false);
            });
        }
      } else {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: msgErroValidacao,
        });
        setIsLoadingSalvarDadosRecolhimento(false);
      }
    }
  };

  const selecionarOpcaoPagamento = () => {
    setIsModalOpcoesPagamentoOpened(true);
  };

  const avancarFinalizarAction = () => {
    switch (step) {
      case 2:
        enviarDadosRecolhimento();
        break;
      case 4:
        selecionarOpcaoPagamento();
        break;
      default:
        setStep(step + 1);
        break;
    }
  };

  const excluirGuiaRecolhimento = async () => {
    await deleteGuiaRecolhimento(guiaRecolhimentoId)
      .then(() => {
        setTimeout(() => {
          mostrarNotificacao({
            tipo: 'success',
            conteudo: 'Guia de recolhimento excluída com sucesso',
          });
        }, 100);

        window.scrollTo(0, 0);
        navigate('/');
      })
      .catch((err) => {
        setIsModalOpened(false);
        if (err.status === 404) {
          navigate('/');
        } else {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao excluir a guia de recolhimento, tente novamente mais tarde. Erro: ' + err.message,
          });
        }
      });
  };

  const copiarGuiaRecolhimento = async (isReemissao?: boolean) => {
    setIsLoadingCopiarGuia(true);
    await getCopiarGuia(guiaRecolhimentoId)
      .then((res) => {
        let novaGuiaId = res;
        setTimeout(() => {
          mostrarNotificacao({
            tipo: 'success',
            conteudo: 'Guia de recolhimento copiada com sucesso',
          });
        }, 100);
        if (isReemissao) {
          reemitirGuiaRecolhimento(res);
          return;
        }
        window.scrollTo(0, 0);
        setDadosRecolhimento((prev) => ({
          ...prev,
          id: guiaRecolhimentoId,
          tipoDeclarante: 0,
          cpfCnpjDeclarante: userCpfCnpj || '',
          competenciaMes: 0,
          competenciaAno: 0,
          numeroProcesso: '',
          substancia: '',
          municipio: -1,
          tipoRelacao: -1,
          tipoRecolhimento: -1,
          observacoes: '',
        }));
        setStep(2);
        setIsLoadingCopiarGuia(false);
        navigate(`/titular-ou-arrendatario?id=${novaGuiaId}`);
      })
      .catch((err) => {
        setIsLoadingCopiarGuia(false);
        if (err.status === 404) {
          navigate('/');
        } else {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao copiar a guia de recolhimento, tente novamente mais tarde. Erro: ' + err.message,
          });
        }
      });
  };

  const reemitirGuiaRecolhimento = async (novaGuiaId: string) => {
    await getDadosRecolhimento(novaGuiaId)
      .then((res) => {
        setDadosRecolhimento({
          id: res.id,
          tipoDeclarante: ([0, 1, 2].includes(Number(res.tipoDeclarante)) ? Number(res.tipoDeclarante) : 0) as
            | 0
            | 1
            | 2,
          cpfCnpjDeclarante: userCpfCnpj || '',
          competenciaMes: res.competenciaMes,
          competenciaAno: res.competenciaAno,
          numeroProcesso: res.numeroProcesso,
          substancia: res.substancia,
          municipio: res.municipio.codigoIBGE,
          tipoRelacao: res.tipoRelacao,
          tipoRecolhimento: -1,
          observacoes: res.observacoes,
        });
        setStep(4);
        setIsLoadingCopiarGuia(false);
        navigate(`/titular-ou-arrendatario?id=${novaGuiaId}`);
      })
      .catch((err) => {
        setIsLoadingCopiarGuia(false);
        mostrarNotificacao({
          tipo: 'danger',
          conteudo:
            'Erro ao consultar a guia de recolhimento reemitida, consulte a guia na listagem e continue a operação. Erro: ' +
            err.message,
        });
        navigate(`/`);
      });
    return;
  };

  return (
    <>
      <Modal title='Excluir Guia de Recolhimento' useScrim modalOpened={isModalOpened}>
        <Modal.Body>
          Deseja mesmo deletar <strong>TODA</strong> a guia de recolhimento? Esse processo{' '}
          <strong>não tem volta</strong> e excluirá todas as bases de cálculo vinculadas a essa guia.
        </Modal.Body>
        <Modal.Footer justify-content='end'>
          <Button secondary small m={2} onClick={() => setIsModalOpened(false)}>
            Cancelar
          </Button>
          <CustomConfirmer asyncCall={() => excluirGuiaRecolhimento()}>Confirmar</CustomConfirmer>
        </Modal.Footer>
      </Modal>
      <ModalOpcoesPagamento
        title='Meios de pagamento'
        isModalOpened={isModalOpcoesPagamentoOpened}
        setIsModalOpened={setIsModalOpcoesPagamentoOpened}
        setStep={setStep}
        step={step}
        opcaoPagamento={opcaoPagamento}
        setOpcaoPagamento={setOpcaoPagamento}
        maxStep={steps.length}
      />
      <BrDivider className={styles.divider} />
      <TituloSection type='titular-arrendatario' isSteps />
      <CustomDivider />
      {isLoadingPermissaoAcesso && (
        <div className={styles.containerPrincipal}>
          <div className={styles.containerAvulso}>
            <BrLoading large />
          </div>
        </div>
      )}
      {!isLoadingPermissaoAcesso && permissaoAcessoError && permissaoAcessoError?.status !== 404 && (
        <div className={styles.containerPrincipal}>
          <div className={styles.containerAvulso}>
            <ErrorMessage
              errorDescription={
                permissaoAcessoError?.status === 403
                  ? 'Essa guia de recolhimento não pertence ao CPF/CNPJ selecionado, por favor verifique.'
                  : 'Erro ao buscar guia de recolhimento, tente novamente clicando no botão abaixo.'
              }
              errorMessage={permissaoAcessoError?.message || ''}
              error={permissaoAcessoError}
              errorTitle={
                permissaoAcessoError?.status === 403 ? 'Acesso Negado' : 'Erro ao buscar guia de recolhimento'
              }
              buttonMessage={permissaoAcessoError?.status === 403 ? 'Retornar à lista de guias' : 'Tentar novamente'}
              onClickFunction={
                permissaoAcessoError?.status === 403 ? () => navigate('/') : () => permissaoAcessoRefetch()
              }
              noBorder
            />
          </div>
        </div>
      )}
      {!isLoadingPermissaoAcesso && (permissaoAcessoError?.status === 404 || permissaoStatus === 202) && (
        <div className={styles.containerPrincipal}>
          <div className={styles.stepContainer}>
            <CustomStep steps={steps} setStepFormulario={setStep} stepAtual={step} />
          </div>
          {isAdicaoNovaBaseCalculo && <div className={styles.baseDeCalculoContainer}>{baseDeCalculo()}</div>}
          {!isAdicaoNovaBaseCalculo && (
            <div className={styles.contentContainer}>
              <div className={styles.formContainer}>
                <div className={styles.titleContainer}>
                  <h4 className={styles.title}>Nova guia de recolhimento da CFEM</h4>
                  <BrButton
                    secondary
                    className={styles.buttonHelp}
                    size='small'
                    icon='fas fa-list'
                    data-tooltip-id='button-retornar-lista-guias'
                    onClick={() => navigate('/')}
                  >
                    Retornar a Lista de Guias
                  </BrButton>
                  <Tooltip
                    id='button-retornar-lista-guias'
                    place='bottom-end'
                    className={styles.tooltip}
                    variant='info'
                  >
                    <div className={styles.tooltipText}>
                      Essa ação retornará à lista de guias sem excluir a guia atual, mantendo-a em rascunho.
                    </div>
                  </Tooltip>
                </div>
                <StepFormAccordionConfig
                  key={guiaRecolhimentoId}
                  step={step}
                  setStep={setStep}
                  stepDoisContent={
                    <DadosRecolhimento
                      isViewOnly={step !== 2}
                      dadosRecolhimento={dadosRecolhimento}
                      setDadosRecolhimento={setDadosRecolhimento}
                      isEdicaoDadosRecolhimento={isEdicaoDadosRecolhimento}
                      setIsEdicaoDadosRecolhimento={setIsEdicaoDadosRecolhimento}
                      stepAtual={step}
                    />
                  }
                  stepTresContent={
                    <DadosOperacoes
                      setIsAdicaoNovaBaseCalculo={setIsAdicaoNovaBaseCalculo}
                      isViewOnly={step !== 3}
                      setBaseDeCalculoEdicao={setBaseDeCalculoEdicao}
                      setIsEdicaoBaseCalculo={setIsEdicaoBaseCalculo}
                    />
                  }
                  stepQuatroContent={
                    <ResumoSection
                      step={step}
                      opcaoPagamento={opcaoPagamento}
                      setIsModalOpcoesPagamentoOpened={setIsModalOpcoesPagamentoOpened}
                      copiarGuiaRecolhimento={copiarGuiaRecolhimento}
                    />
                  }
                />
              </div>
              {step !== 5 && (
                <div className={styles.buttonContainer}>
                  <div className={styles.leftButtons}>
                    <BrButton className={styles.botao} icon='trash' onClick={() => setIsModalOpened(true)} secondary>
                      Excluir
                    </BrButton>
                  </div>
                  <div className={styles.rightButtons}>
                    {step !== 1 && (
                      <BrButton
                        className={styles.botao}
                        icon='arrow-left'
                        onClick={() => {
                          setStep(step - 1);
                          window.scrollTo(0, 300);
                        }}
                      >
                        Voltar
                      </BrButton>
                    )}

                    <BrButton
                      primary
                      className={styles.botao}
                      icon={step === 4 ? 'donate' : 'arrow-right'}
                      onClick={() => avancarFinalizarAction()}
                      loading={isLoadingSalvarDadosRecolhimento}
                    >
                      {step === 4 ? 'Finalizar e Emitir Guia de Recolhimento' : 'Salvar e Avançar'}
                    </BrButton>
                  </div>
                </div>
              )}
              {step === 5 && (
                <div className={styles.buttonContainer}>
                  <div className={styles.leftButtons}></div>
                  <div className={styles.rightButtons}>
                    <BrButton
                      primary
                      className={styles.botao}
                      icon='fas fa-edit'
                      onClick={() => copiarGuiaRecolhimento()}
                      loading={isLoadingCopiarGuia}
                    >
                      Copiar dados da Guia
                    </BrButton>

                    <BrButton primary className={styles.botao} onClick={() => navigate('/')}>
                      Ir para a listagem de guias
                    </BrButton>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default TitularArrendatarioEdit;
